/**
 * MultiSelect Component
 * Multi-selector for genres/categories
 * 
 * @author Varun Pandey
 */

import React, { useMemo, useState } from 'react';
import { cn } from '../../utils/cn';

export interface MultiSelectOption {
  value: string;
  label: string;
}

/**
 * `defaultPrimaryValues` always appear in the first row (selected or not).
 * Any other selected option is also shown in that row; the rest stay under “See more”.
 */
export interface MultiSelectSeeMoreCollapse {
  defaultPrimaryValues: string[];
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
  label?: string;
  error?: string;
  /** When true, error message is not rendered (e.g. when showing errors via toast). */
  hideErrorText?: boolean;
  className?: string;
  /**
   * Split options into a primary row and a “See more” section.
   * Primary: `defaultPrimaryValues` (in order) plus selected values not in that list (catalog order).
   * Secondary: everything else (unselected non-defaults), so “See more” stays meaningful.
   */
  seeMoreCollapse?: MultiSelectSeeMoreCollapse;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value = [],
  onChange,
  label,
  error,
  hideErrorText = false,
  className,
  seeMoreCollapse,
}) => {
  const [seeMoreOpen, setSeeMoreOpen] = useState(false);

  const toggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange?.(newValue);
  };

  const { primaryOptions, secondaryOptions } = useMemo(() => {
    if (!seeMoreCollapse) {
      return { primaryOptions: options, secondaryOptions: [] as MultiSelectOption[] };
    }
    const byValue = new Map(options.map((o) => [o.value, o]));
    const order = options.map((o) => o.value);
    const defaultSet = new Set(seeMoreCollapse.defaultPrimaryValues);
    const primaryDefaults = seeMoreCollapse.defaultPrimaryValues.filter((v) =>
      byValue.has(v),
    );
    const selectedBeyondDefaults = order.filter(
      (v) => value.includes(v) && !defaultSet.has(v),
    );
    const primaryValues = [...primaryDefaults, ...selectedBeyondDefaults];
    const primaryDisplaySet = new Set(primaryValues);
    const secondaryValues = order.filter((v) => !primaryDisplaySet.has(v));
    return {
      primaryOptions: primaryValues
        .map((v) => byValue.get(v))
        .filter((o): o is MultiSelectOption => o != null),
      secondaryOptions: secondaryValues
        .map((v) => byValue.get(v))
        .filter((o): o is MultiSelectOption => o != null),
    };
  }, [options, seeMoreCollapse, value]);

  const renderOptionButton = (option: MultiSelectOption) => {
    const isSelected = value.includes(option.value);
    return (
      <button
        key={option.value}
        type="button"
        onClick={() => toggleOption(option.value)}
        className={cn(
          'rounded-full md:px-4 md:py-2 px-3 py-1.5 text-xs md:text-sm font-medium transition-colors',
          isSelected
            ? 'bg-[#F33363] text-white'
            : 'bg-[#2C2C2E] text-white hover:bg-[#3A3A3C]',
        )}
      >
        {option.label}
      </button>
    );
  };

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="mb-2 block text-xs font-medium text-[#8E8E93] uppercase tracking-wider">
          {label}
        </label>
      )}
      <div className="space-y-3">
        {/* Selected items as chips (when any selected) */}
        {/* {value.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {value.map((selectedValue) => {
              const option = options.find((opt) => opt.value === selectedValue);
              if (!option) return null;
              return (
                <span
                  key={selectedValue}
                  className="inline-flex items-center gap-1 rounded-full bg-[#F33363] px-3 py-1 text-xs font-medium text-white"
                >
                  {option.label}
                  <button
                    type="button"
                    onClick={() => toggleOption(selectedValue)}
                    className="ml-1 hover:opacity-70"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )} */}
        {seeMoreCollapse ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              {primaryOptions.map(renderOptionButton)}
            </div>
            {secondaryOptions.length > 0 ? (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setSeeMoreOpen((o) => !o)}
                  className="text-xs md:text-sm font-medium text-[#F33363] hover:text-[#ff5a7d] transition-colors"
                >
                  {seeMoreOpen ? 'See less' : 'See more'}
                </button>
                {seeMoreOpen ? (
                  <div className="flex flex-wrap gap-3 border-t border-[#3A3A3C]/40 pt-3">
                    {secondaryOptions.map(renderOptionButton)}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {primaryOptions.map(renderOptionButton)}
          </div>
        )}
      </div>
      {error && !hideErrorText && (
        <p className="mt-2 text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
};
